import { z } from 'zod'
import { designVariantSchema } from '../../../store/feature-flags/useLayoutSettingsStore'

type BlockProps = z.infer<typeof blockPropsSchema>

export type BlockBase = z.infer<typeof blockBaseSchema>

export type SliderBlock = BlockBase & {
  type: 'slider'
  props?: BlockProps
}

export type CarouselBlock = BlockBase & {
  type: 'carousel'
  props?: BlockProps
}

export type TitleBlock = BlockBase & {
  type: 'title' | 'title-l' | 'title-msn'
  props?: BlockProps
}

export type AdTileType = BlockBase['type']

export type VideoBlock = BlockBase & {
  type: 'video' | 'video-s' | 'video-l'
  props?: BlockProps
}

export type ArticleGroupBlock = BlockBase & {
  type: 'article-group'
  props?: BlockProps
}
export type YoloBlock = BlockBase & {
  type: 'yolo-l' | 'yolo-xl' | 'yolo-xxl' | 'yolo-full'
  props?: BlockProps
}

export type AdBlock = z.infer<typeof adBlockSchema>

export type Block = z.infer<typeof blockSchema>

export type GenericElement = {
  type: BlockBase['type']
  ad?: boolean
  onlyWhenGridIsGreaterThan?: number
  x?: number
  y?: number
  classes?: string
  index?: number
  props: BlockProps
  size?: number
}

export type LayoutSection = z.infer<typeof layoutSectionSchema>
export type LayoutSkeleton = z.infer<typeof layoutSkeletonSchema>

/**
 * Article Group props
design: ‘msn’ or ‘google’
pages: number of pages for each article group with msn design on the homepage. The default value for pages is 1, with 3 articles per page. If we don't set the design to be 'msn' the field pages won't have any effect.
{ y: 1, type: 'article-group' , props: { design: 'msn', pages: 6} }
 */
export const blockPropsSchema = z
  .object({
    design: designVariantSchema.default('msn').optional(),
    pages: z.number().min(2).default(6).optional(), // This will only be used for the article slider and for article group on msn design. If we pass this prop for other components, it will be ignored.
  })
  .passthrough()

/**
   * Section
    Each object inside the section array can have:
    name: a name for that section
    title: the title of the section
    background: the background of the section
    padded (boolean): if the section should be padded
    classes: CSS classes that will be added to the section div
    repeat (boolean): if this section should be repeated on the infinite scroll
    titles: the news tile configuration
    ads: the ad tile configuration

    Titles
    type: can be  ‘article-group', ‘slider', ‘title’, 'video', 'yolo-l', 'yolo-xl' or yolo-xxl’, 'carousel’
    y: the row
    x: the column
    repeat: if this title should be repeated to complete the row
    size
    classes: CSS classes that will be added to the block
    props: an object with the design and other possible configurations depending on the news tile type
    onlyWhenGridIsGreaterThan (number) - if you only want that news tile to be shown when you have at least that number of columns available. the maximum size of the grid is 5 columns
   */
const blockBaseSchema = z.object({
  type: z.optional(
    z.union([
      z.literal('slider'),
      z.literal('carousel'),
      z.literal('title'),
      z.literal('title-l'),
      z.literal('video'),
      z.literal('video-s'),
      z.literal('video-l'),
      z.literal('title-msn'),
      z.literal('article-group'),
      z.literal('yolo-xxl'),
      z.literal('yolo-xl'),
      z.literal('yolo-l'),
      z.literal('yolo-full'),
    ])
  ),
  y: z.number(),
  x: z.optional(z.number()),
  repeat: z.optional(z.boolean()),
  size: z.optional(z.number()),
  classes: z.optional(z.string()),
  props: z.optional(blockPropsSchema),
  onlyWhenGridIsGreaterThan: z.optional(z.number()),
})

const adBlockSchema = z.object({
  y: z.number(),
  x: z.optional(z.number()),
  classes: z.optional(z.string()),
  props: z.optional(blockPropsSchema),
  onlyWhenGridIsGreaterThan: z.optional(z.number()),
})

/**
 * Article Slider props
 * contentType: ‘video’ or ‘title’ (default)
  design: ‘msn’ or ‘google’
  pages:  number of articles for each slider on the homepage. The default value is 6, with 1 news per page/slide.
  autoplay: if the slides will be automatically
  autoplayInterval: how long should the slide be shown before moving on to the next one in milliseconds
 */
const sliderBlockSchema = blockBaseSchema.extend({
  type: z.literal('slider'),
  props: z.optional(
    blockPropsSchema.extend({
      autoplayInterval: z.optional(z.number()),
      ads: z.optional(
        z.array(
          adBlockSchema
            .pick({ classes: true, props: true })
            .extend({ position: z.number() })
        )
      ),
      contentType: z.enum(['video', 'title']).optional(),
    })
  ),
})

const blockSchema = z.union([blockBaseSchema, sliderBlockSchema, adBlockSchema])

const layoutSectionSchema = z.object({
  name: z.string(),
  title: z.optional(z.string()),
  titleIcon: z.optional(z.string()),
  background: z.optional(z.string()),
  padded: z.optional(z.literal(false)),
  classes: z.optional(z.string()),
  repeat: z.optional(z.literal(true)),
  titles: z.optional(z.array(blockSchema)),
  ads: z.optional(z.array(blockSchema)),
  onlyWhenGridIsGreaterThan: z.optional(z.number()),
})

export const layoutSkeletonSchema = z.object({
  name: z.string(),
  section: z.array(layoutSectionSchema),
})

export type ComputedLayout = {
  id: string
  background?: string
  padded?: false
  classes?: string
  repeat?: boolean
  title?: string
  titleIcon?: string
  content: GenericElement[][]
}
